<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue"
import { useBreakpoints } from "~/composables/useBreakpoints"
import scssVariables from "~/assets/scss/export.module.scss"

const props = defineProps({
  // Gap between items in pixels (M3 default: 8dp)
  gap: {
    type: Number,
    default: 8,
  },
  // Apply gradient mask on left/right edges
  edgeFade: {
    type: Boolean,
    default: true,
  },
  // Width of edge fade gradient in pixels
  edgeFadeDistance: {
    type: Number,
    default: 40,
  },
  // nav arrows position >=xl
  xlTop: {
    type: String,
    default: "50%",
  },
  // nav arrows position <lg
  lgTop: {
    type: String,
    default: "50%",
  },
  // nav arrows position <md
  mdTop: {
    type: String,
    default: "50%",
  },
})

const carouselRef = ref(null)
const trackRef = ref(null)
const currentScrollLeft = ref(0)
const maxScrollLeft = ref(0)
const containerWidth = ref(0)
const showLeftArrow = ref(false)
const showRightArrow = ref(true)

// Resize observing
let resizeObserver = null

// Breakpoint logic for snap buffer
const { breakpoint } = useBreakpoints()

const marginBuffer = computed(() => {
  // Parse SCSS values (e.g. "1.5rem" -> pixels)
  const getPx = (val) => {
    if (!val) return 0
    if (val.endsWith("px")) return parseFloat(val)
    if (val.endsWith("rem")) {
      // Approximate or assume root 16px
      return parseFloat(val) * 16
    }
    return parseFloat(val)
  }

  const mobilePadding = getPx(scssVariables.padding)
  const desktopPadding = getPx(scssVariables.paddingDesktop)

  // Match SCSS logic:
  // >= xxl -> 0
  // >= md -> desktopPadding
  // else -> mobilePadding
  if (breakpoint(">=xxl")) return 0
  if (breakpoint(">=md")) return desktopPadding
  return mobilePadding
})

// Edge fade gradient mask style - Dynamic based on scroll position
const maskStyle = computed(() => {
  if (!props.edgeFade) return {}

  const fadeDistance = props.edgeFadeDistance
  const isStart = currentScrollLeft.value <= 5 // Tolerance
  const isEnd = currentScrollLeft.value >= maxScrollLeft.value - 5 // Tolerance

  let maskString = ""

  if (isStart && isEnd) {
    // Content fits, no mask needed usually, or full visibility
    return {}
  } else if (isStart) {
    // Only right mask
    maskString = `linear-gradient(to right, black calc(100% - ${fadeDistance}px), transparent 100%)`
  } else if (isEnd) {
    // Only left mask
    maskString = `linear-gradient(to right, transparent 0, black ${fadeDistance}px)`
  } else {
    // Both masks
    maskString = `linear-gradient(to right, transparent 0, black ${fadeDistance}px, black calc(100% - ${fadeDistance}px), transparent 100%)`
  }

  return {
    maskImage: maskString,
    WebkitMaskImage: maskString,
  }
})

// Scroll Handler
const onScroll = () => {
  if (!trackRef.value) return
  currentScrollLeft.value = trackRef.value.scrollLeft

  // Update Arrow State
  showLeftArrow.value = currentScrollLeft.value > 5
  // Allowing a small buffer for float precision
  showRightArrow.value = currentScrollLeft.value < maxScrollLeft.value - 5
}

// Navigation Logic
const isAnimating = ref(false)

// Helper to unlock animation state
const unlockAnimation = () => {
  isAnimating.value = false
}
// Get snap position based on target position and direction
const getSnapPosition = (targetPos, direction) => {
  if (!trackRef.value) return targetPos
  const children = Array.from(trackRef.value.children)

  // Find closest item start to targetPos
  // But strictly respecting direction relative to current position

  let bestPos = targetPos
  let minDiff = Infinity

  // Safety buffer
  const current = currentScrollLeft.value
  const buffer = 10
  const bufferOffset = marginBuffer.value

  for (const child of children) {
    // Adjust pos to account for the visual buffer we want to maintain
    // So if child starts at 100, and buffer is 20, we want to snap to 80 (so child starts at 20 visual)
    const pos = Math.max(0, child.offsetLeft - bufferOffset)

    // Direction constraint
    if (direction === "next" && pos <= current + buffer) continue
    if (direction === "prev" && pos >= current - buffer) continue

    // Find closest snap point to the ideal target
    const diff = Math.abs(pos - targetPos)
    if (diff < minDiff) {
      minDiff = diff
      bestPos = pos
    }
  }

  // Boundary checks if nothing found (should rarely happen if target is reasonable)
  if (minDiff === Infinity) {
    return direction === "next" ? maxScrollLeft.value : 0
  }

  return bestPos
}
// Scroll to previous item
const scrollToPrev = () => {
  if (!trackRef.value) return
  isAnimating.value = true

  const containerW = containerWidth.value
  const idealTarget = currentScrollLeft.value - containerW * 0.9
  const targetScroll = getSnapPosition(Math.max(0, idealTarget), "prev")

  trackRef.value.scrollTo({
    left: targetScroll,
    behavior: "smooth",
  })

  // Safety timeout for unlock if scrollend doesn't fire
  setTimeout(unlockAnimation, 600)
}
// Scroll to next item
const scrollToNext = () => {
  if (!trackRef.value) return
  isAnimating.value = true

  const containerW = containerWidth.value
  const idealTarget = currentScrollLeft.value + containerW * 0.9
  const targetScroll = getSnapPosition(
    Math.min(maxScrollLeft.value, idealTarget),
    "next"
  )

  trackRef.value.scrollTo({
    left: targetScroll,
    behavior: "smooth",
  })

  // Safety timeout for unlock if scrollend doesn't fire
  setTimeout(unlockAnimation, 600)
}

// Update metrics logic
const updateMetrics = () => {
  if (!trackRef.value || !carouselRef.value) return

  containerWidth.value = carouselRef.value.offsetWidth
  // maxScrollLeft is total scroll width minus visible width
  maxScrollLeft.value = trackRef.value.scrollWidth - trackRef.value.clientWidth

  onScroll()
}

let mutationObserver = null
let mutationRafId = null

onMounted(() => {
  if (carouselRef.value) {
    containerWidth.value = carouselRef.value.offsetWidth
    resizeObserver = new ResizeObserver((entries) => {
      // Wrap in rAF to avoid "ResizeObserver loop limit exceeded"
      requestAnimationFrame(() => {
        if (!entries.length) return
        updateMetrics()
      })
    })
    resizeObserver.observe(carouselRef.value)
  }

  if (trackRef.value) {
    mutationObserver = new MutationObserver(() => {
      // Debounce via rAF to avoid forced reflows during hydration cascades
      if (mutationRafId) return
      mutationRafId = requestAnimationFrame(() => {
        mutationRafId = null
        updateMetrics()
      })
    })
    mutationObserver.observe(trackRef.value, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    // Initial Scroll Check
    updateMetrics()

    // Add scrollend listener for robust unlock
    trackRef.value.addEventListener("scrollend", unlockAnimation)
  }

  // Double check after a tick to ensure layout is done
  setTimeout(updateMetrics, 100)
})

onBeforeUnmount(() => {
  if (trackRef.value) {
    trackRef.value.removeEventListener("scrollend", unlockAnimation)
  }
  if (mutationRafId) cancelAnimationFrame(mutationRafId)
  if (mutationObserver) mutationObserver.disconnect()
  if (resizeObserver) resizeObserver.disconnect()
})
</script>

<template>
  <div
    class="material-carousel-basic"
    ref="carouselRef"
    :style="maskStyle"
    role="region"
    aria-label="carousel"
  >
    <!-- Left Arrow -->
    <button
      v-show="showLeftArrow"
      class="nav-arrow nav-arrow-left style-mode-light"
      @click="scrollToPrev"
      aria-label="Previous items"
    >
      <i class="pi pi-chevron-left" aria-hidden="true"></i>
    </button>

    <div
      class="carousel-track"
      ref="trackRef"
      @scroll="onScroll"
      :style="{
        gap: `${gap}px`,
      }"
    >
      <slot />
    </div>

    <!-- Right Arrow -->
    <button
      v-show="showRightArrow"
      class="nav-arrow nav-arrow-right style-mode-light"
      @click="scrollToNext"
      aria-label="Next items"
    >
      <i class="pi pi-chevron-right" aria-hidden="true"></i>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.material-carousel-basic {
  overflow: hidden;
  position: relative;
  display: grid;
  align-items: center;

  $arrow-distance: 20px;

  @mixin arrow-spacing($paddingVal) {
    @if $paddingVal == 0 {
      margin-left: 0;
      margin-right: 0;

      .carousel-track :deep(> *:first-child) {
        margin-left: 0;
      }
      .carousel-track :deep(> *:last-child) {
        margin-right: 0;
      }
      .nav-arrow-left {
        left: #{$arrow-distance} !important;
      }
      .nav-arrow-right {
        right: #{$arrow-distance} !important;
      }
    } @else {
      margin-left: calc(#{$paddingVal} * -1);
      margin-right: calc(#{$paddingVal} * -1);

      .carousel-track :deep(> *:first-child) {
        margin-left: $paddingVal;
      }
      .carousel-track :deep(> *:last-child) {
        margin-right: $paddingVal;
      }
      .nav-arrow-left {
        left: calc(#{$arrow-distance} + #{$paddingVal}) !important;
      }
      .nav-arrow-right {
        right: calc(#{$arrow-distance} + #{$paddingVal}) !important;
      }
    }
  }

  // Default (Mobile)
  @include arrow-spacing($padding);

  @include media(">=md") {
    @include arrow-spacing($paddingDesktop);
  }

  @include media(">=xxl") {
    // Reset to contained
    @include arrow-spacing(0);
  }
}

.carousel-track {
  display: flex;
  flex-direction: row;
  align-items: stretch;

  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  width: 100%;

  // Hide scrollbar
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

// Style slotted items using deep selector
.carousel-track :deep(> *) {
  flex: 0 0 auto;
  width: auto;
  height: auto;
  box-sizing: border-box;
  overflow: hidden;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block; // Fix bottom gap
  }
}

.nav-arrow {
  position: absolute;
  z-index: 10;
  transform: translateY(-50%);
  top: v-bind(xlTop);
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    background: #f8f8f8;
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  @include media("<lg") {
    top: v-bind(lgTop);
  }
  @include media("<md") {
    top: v-bind(mdTop);
  }
}
</style>
